// src/components/Branch/config/sections/LocationSection.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LocationMarkerIcon,
  MapIcon,
  PencilIcon,
  CheckCircleIcon,
  ExternalLinkIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import {
  updateStoreDirectionsThunk,
  updateAddressThunk,
} from "../../../../store/thunks/branchDisplayThunks";

const PARKING_TYPES = [
  { _id: "FREE", name: "Free Parking" },
  { _id: "PAID", name: "Paid Parking" },
  { _id: "VALIDATED", name: "Validated Parking" },
  { _id: "VALET", name: "Valet Parking" },
];

// Indian states for dropdown
const INDIAN_STATES = [
  { code: "AN", name: "Andaman and Nicobar Islands" },
  { code: "AP", name: "Andhra Pradesh" },
  { code: "AR", name: "Arunachal Pradesh" },
  { code: "AS", name: "Assam" },
  { code: "BR", name: "Bihar" },
  { code: "CH", name: "Chandigarh" },
  { code: "CT", name: "Chhattisgarh" },
  { code: "DN", name: "Dadra and Nagar Haveli" },
  { code: "DD", name: "Daman and Diu" },
  { code: "DL", name: "Delhi" },
  { code: "GA", name: "Goa" },
  { code: "GJ", name: "Gujarat" },
  { code: "HR", name: "Haryana" },
  { code: "HP", name: "Himachal Pradesh" },
  { code: "JK", name: "Jammu and Kashmir" },
  { code: "JH", name: "Jharkhand" },
  { code: "KA", name: "Karnataka" },
  { code: "KL", name: "Kerala" },
  { code: "LA", name: "Ladakh" },
  { code: "LD", name: "Lakshadweep" },
  { code: "MP", name: "Madhya Pradesh" },
  { code: "MH", name: "Maharashtra" },
  { code: "MN", name: "Manipur" },
  { code: "ML", name: "Meghalaya" },
  { code: "MZ", name: "Mizoram" },
  { code: "NL", name: "Nagaland" },
  { code: "OR", name: "Odisha" },
  { code: "PY", name: "Puducherry" },
  { code: "PB", name: "Punjab" },
  { code: "RJ", name: "Rajasthan" },
  { code: "SK", name: "Sikkim" },
  { code: "TN", name: "Tamil Nadu" },
  { code: "TG", name: "Telangana" },
  { code: "TR", name: "Tripura" },
  { code: "UP", name: "Uttar Pradesh" },
  { code: "UT", name: "Uttarakhand" },
  { code: "WB", name: "West Bengal" },
];

const LocationSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const {
    storeInfo = {},
    address = {},
    loadingSection,
  } = useSelector((state) => state.branchDisplay);

  // Editing states
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isEditingDirections, setIsEditingDirections] = useState(false);
  const [isEditingLandmarks, setIsEditingLandmarks] = useState(false);
  const [isEditingParking, setIsEditingParking] = useState(false);

  // Form states
  const [addressForm, setAddressForm] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    pinCode: "",
    landmark: "",
    latitude: "",
    longitude: "",
    googleMapsUrl: "",
    googlePlaceId: "",
  });

  const [directionsForm, setDirectionsForm] = useState({
    fromRailway: "",
    fromAirport: "",
    fromBusStand: "",
    fromMetro: "",
    publicTransport: "",
    parkingInfo: "",
    additionalInfo: "",
  });

  const [landmarks, setLandmarks] = useState([]);
  const [newLandmark, setNewLandmark] = useState("");

  const [parkingForm, setParkingForm] = useState({
    available: false,
    type: "",
    twoWheeler: false,
    fourWheeler: false,
    capacity: "",
    instructions: "",
  });

  // Initialize forms from store data
  useEffect(() => {
    if (address && Object.keys(address).length > 0) {
      setAddressForm({
        line1: address.line1 || "",
        line2: address.line2 || "",
        city: address.city || "",
        state: address.state || "",
        pinCode: address.pinCode || "",
        landmark: address.landmark || "",
        latitude: address.latitude || "",
        longitude: address.longitude || "",
        googleMapsUrl: address.googleMapsUrl || "",
        googlePlaceId: address.googlePlaceId || "",
      });
    }
  }, [address]);

  useEffect(() => {
    if (storeInfo) {
      setDirectionsForm({
        fromRailway: storeInfo.directions?.fromRailway || "",
        fromAirport: storeInfo.directions?.fromAirport || "",
        fromBusStand: storeInfo.directions?.fromBusStand || "",
        fromMetro: storeInfo.directions?.fromMetro || "",
        publicTransport: storeInfo.directions?.publicTransport || "",
        parkingInfo: storeInfo.directions?.parkingInfo || "",
        additionalInfo: storeInfo.directions?.additionalInfo || "",
      });
      setLandmarks(
        Array.isArray(storeInfo.nearbyLandmarks)
          ? storeInfo.nearbyLandmarks
          : []
      );
      setParkingForm({
        available: storeInfo.parking?.available || false,
        type: storeInfo.parking?.type || "",
        twoWheeler: storeInfo.parking?.twoWheeler || false,
        fourWheeler: storeInfo.parking?.fourWheeler || false,
        capacity: storeInfo.parking?.capacity || "",
        instructions: storeInfo.parking?.instructions || "",
      });
    }
  }, [storeInfo]);

  // Handlers
  const handleSaveAddress = async () => {
    // Validate required fields
    if (
      !addressForm.line1 ||
      !addressForm.city ||
      !addressForm.state ||
      !addressForm.pinCode
    ) {
      alert(
        "Please fill in all required fields: Address Line 1, City, State, and PIN Code"
      );
      return;
    }

    // Validate PIN code format
    if (!/^[1-9][0-9]{5}$/.test(addressForm.pinCode)) {
      alert("Please enter a valid 6-digit PIN code");
      return;
    }

    await dispatch(
      updateAddressThunk({
        branchCode,
        addressData: {
          line1: addressForm.line1,
          line2: addressForm.line2,
          city: addressForm.city,
          state: addressForm.state,
          pinCode: addressForm.pinCode,
          landmark: addressForm.landmark,
          latitude: addressForm.latitude
            ? parseFloat(addressForm.latitude)
            : null,
          longitude: addressForm.longitude
            ? parseFloat(addressForm.longitude)
            : null,
          googleMapsUrl: addressForm.googleMapsUrl,
          googlePlaceId: addressForm.googlePlaceId,
        },
      })
    );
    setIsEditingAddress(false);
  };

  const handleSaveDirections = async () => {
    await dispatch(
      updateStoreDirectionsThunk({
        branchCode,
        directions: directionsForm,
        nearbyLandmarks: landmarks,
        parking: parkingForm,
      })
    );
    setIsEditingDirections(false);
  };

  const handleSaveLandmarks = async () => {
    await dispatch(
      updateStoreDirectionsThunk({
        branchCode,
        directions: storeInfo.directions || {},
        nearbyLandmarks: landmarks,
        parking: storeInfo.parking || {},
      })
    );
    setIsEditingLandmarks(false);
  };

  const handleSaveParking = async () => {
    await dispatch(
      updateStoreDirectionsThunk({
        branchCode,
        directions: storeInfo.directions || {},
        nearbyLandmarks: storeInfo.nearbyLandmarks || [],
        parking: {
          ...parkingForm,
          capacity: parkingForm.capacity
            ? parseInt(parkingForm.capacity)
            : null,
        },
      })
    );
    setIsEditingParking(false);
  };

  const addLandmark = () => {
    if (newLandmark.trim()) {
      setLandmarks([...landmarks, newLandmark.trim()]);
      setNewLandmark("");
    }
  };

  const removeLandmark = (index) => {
    setLandmarks(landmarks.filter((_, i) => i !== index));
  };

  const openGoogleMaps = () => {
    if (addressForm.latitude && addressForm.longitude) {
      window.open(
        `https://www.google.com/maps?q=${addressForm.latitude},${addressForm.longitude}`,
        "_blank"
      );
    } else if (addressForm.googleMapsUrl) {
      window.open(addressForm.googleMapsUrl, "_blank");
    }
  };

  const getDirectionsLink = () => {
    if (addressForm.latitude && addressForm.longitude) {
      return `https://www.google.com/maps/dir/?api=1&destination=${addressForm.latitude},${addressForm.longitude}`;
    }
    return null;
  };

  const getStateName = (stateCode) => {
    const state = INDIAN_STATES.find((s) => s.code === stateCode);
    return state ? state.name : stateCode;
  };

  return (
    <div className="space-y-6">
      {/* Store Address */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium text-blue-800 flex items-center">
            <LocationMarkerIcon className="h-5 w-5 mr-2" />
            Store Address
          </h4>
          {isRootAdmin && !isEditingAddress && (
            <button
              onClick={() => setIsEditingAddress(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" /> Edit
            </button>
          )}
        </div>

        {isEditingAddress ? (
          <div className="space-y-4 bg-white p-4 rounded-lg">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Address Line 1 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={addressForm.line1}
                  onChange={(e) =>
                    setAddressForm((p) => ({ ...p, line1: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Shop No, Building Name, Street"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={addressForm.line2}
                  onChange={(e) =>
                    setAddressForm((p) => ({ ...p, line2: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Area, Locality"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={addressForm.city}
                  onChange={(e) =>
                    setAddressForm((p) => ({ ...p, city: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={addressForm.state}
                  onChange={(e) =>
                    setAddressForm((p) => ({ ...p, state: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  PIN Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={addressForm.pinCode}
                  onChange={(e) =>
                    setAddressForm((p) => ({
                      ...p,
                      pinCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="6-digit PIN"
                  maxLength={6}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Landmark
              </label>
              <input
                type="text"
                value={addressForm.landmark}
                onChange={(e) =>
                  setAddressForm((p) => ({ ...p, landmark: e.target.value }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Near famous landmark"
              />
            </div>

            <hr className="my-4" />
            <h5 className="font-medium text-gray-700 flex items-center">
              <MapIcon className="h-4 w-4 mr-2" />
              Map & Coordinates
            </h5>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={addressForm.latitude}
                  onChange={(e) =>
                    setAddressForm((p) => ({ ...p, latitude: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., 19.0760"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={addressForm.longitude}
                  onChange={(e) =>
                    setAddressForm((p) => ({ ...p, longitude: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., 72.8777"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Google Maps URL
              </label>
              <input
                type="url"
                value={addressForm.googleMapsUrl}
                onChange={(e) =>
                  setAddressForm((p) => ({
                    ...p,
                    googleMapsUrl: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="https://maps.google.com/..."
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Google Place ID
              </label>
              <input
                type="text"
                value={addressForm.googlePlaceId}
                onChange={(e) =>
                  setAddressForm((p) => ({
                    ...p,
                    googlePlaceId: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="ChIJ..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Used for Google Maps integration and reviews
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSaveAddress}
                disabled={loadingSection === "address"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" />
                {loadingSection === "address" ? "Saving..." : "Save Address"}
              </button>
              <button
                onClick={() => setIsEditingAddress(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-gray-700">
              <p className="font-medium">
                {address?.line1 || "Address not set"}
              </p>
              {address?.line2 && <p>{address.line2}</p>}
              {address?.city && (
                <p>
                  {address.city}, {getStateName(address.state)} -{" "}
                  {address.pinCode}
                </p>
              )}
              {address?.landmark && (
                <p className="text-sm text-gray-500 mt-1">
                  <span className="font-medium">Landmark:</span>{" "}
                  {address.landmark}
                </p>
              )}
            </div>

            {/* Map info display */}
            {(addressForm.latitude || addressForm.googleMapsUrl) && (
              <div className="pt-3 border-t border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {addressForm.latitude && addressForm.longitude && (
                      <span>
                        📍 {addressForm.latitude}, {addressForm.longitude}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={openGoogleMaps}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <ExternalLinkIcon className="h-4 w-4" /> View on Map
                  </button>
                </div>
                {getDirectionsLink() && (
                  <a
                    href={getDirectionsLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline mt-1 block"
                  >
                    Get Directions →
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Directions from Various Points */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">How to Reach</h4>
          {isRootAdmin && !isEditingDirections && (
            <button
              onClick={() => setIsEditingDirections(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" /> Edit
            </button>
          )}
        </div>

        {isEditingDirections ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                From Railway Station
              </label>
              <textarea
                value={directionsForm.fromRailway}
                onChange={(e) =>
                  setDirectionsForm((p) => ({
                    ...p,
                    fromRailway: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Directions from nearest railway station..."
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                From Airport
              </label>
              <textarea
                value={directionsForm.fromAirport}
                onChange={(e) =>
                  setDirectionsForm((p) => ({
                    ...p,
                    fromAirport: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Directions from airport..."
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                From Bus Stand
              </label>
              <textarea
                value={directionsForm.fromBusStand}
                onChange={(e) =>
                  setDirectionsForm((p) => ({
                    ...p,
                    fromBusStand: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Directions from bus stand..."
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                From Metro Station
              </label>
              <textarea
                value={directionsForm.fromMetro}
                onChange={(e) =>
                  setDirectionsForm((p) => ({
                    ...p,
                    fromMetro: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Directions from nearest metro station..."
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Public Transport Options
              </label>
              <textarea
                value={directionsForm.publicTransport}
                onChange={(e) =>
                  setDirectionsForm((p) => ({
                    ...p,
                    publicTransport: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Bus routes, auto stands, etc..."
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Additional Info
              </label>
              <textarea
                value={directionsForm.additionalInfo}
                onChange={(e) =>
                  setDirectionsForm((p) => ({
                    ...p,
                    additionalInfo: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Any other helpful navigation info..."
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSaveDirections}
                disabled={loadingSection === "directions"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" /> Save
              </button>
              <button
                onClick={() => setIsEditingDirections(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {[
              { label: "From Railway", value: directionsForm.fromRailway },
              { label: "From Airport", value: directionsForm.fromAirport },
              { label: "From Bus Stand", value: directionsForm.fromBusStand },
              { label: "From Metro", value: directionsForm.fromMetro },
              {
                label: "Public Transport",
                value: directionsForm.publicTransport,
              },
              {
                label: "Additional Info",
                value: directionsForm.additionalInfo,
              },
            ].map(
              (item, index) =>
                item.value && (
                  <div key={index} className="border-l-2 border-blue-300 pl-3">
                    <span className="text-xs text-gray-500 uppercase">
                      {item.label}
                    </span>
                    <p className="text-sm text-gray-700">{item.value}</p>
                  </div>
                )
            )}
            {!directionsForm.fromRailway &&
              !directionsForm.fromAirport &&
              !directionsForm.fromBusStand &&
              !directionsForm.fromMetro && (
                <p className="text-gray-500 text-sm">No directions added yet</p>
              )}
          </div>
        )}
      </div>

      {/* Nearby Landmarks */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">Nearby Landmarks</h4>
          {isRootAdmin && !isEditingLandmarks && (
            <button
              onClick={() => setIsEditingLandmarks(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" /> Edit
            </button>
          )}
        </div>

        {isEditingLandmarks ? (
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newLandmark}
                onChange={(e) => setNewLandmark(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg"
                placeholder="Add a nearby landmark..."
                onKeyPress={(e) => e.key === "Enter" && addLandmark()}
              />
              <button
                onClick={addLandmark}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
              >
                <PlusIcon className="h-4 w-4" /> Add
              </button>
            </div>

            <div className="space-y-2">
              {landmarks.map((landmark, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-white p-2 rounded border"
                >
                  <span className="text-sm">{landmark}</span>
                  <button
                    onClick={() => removeLandmark(index)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {landmarks.length === 0 && (
                <p className="text-gray-500 text-sm">No landmarks added</p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSaveLandmarks}
                disabled={loadingSection === "landmarks"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" /> Save
              </button>
              <button
                onClick={() => setIsEditingLandmarks(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {landmarks.length > 0 ? (
              landmarks.map((landmark, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                >
                  {landmark}
                </span>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No landmarks listed</p>
            )}
          </div>
        )}
      </div>

      {/* Parking Information */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">Parking Information</h4>
          {isRootAdmin && !isEditingParking && (
            <button
              onClick={() => setIsEditingParking(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" /> Edit
            </button>
          )}
        </div>

        {isEditingParking ? (
          <div className="space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={parkingForm.available}
                onChange={(e) =>
                  setParkingForm((p) => ({ ...p, available: e.target.checked }))
                }
                className="h-4 w-4 rounded"
              />
              <span className="text-sm font-medium">Parking Available</span>
            </label>

            {parkingForm.available && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Parking Type
                    </label>
                    <select
                      value={parkingForm.type}
                      onChange={(e) =>
                        setParkingForm((p) => ({ ...p, type: e.target.value }))
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select type</option>
                      {PARKING_TYPES.map((type) => (
                        <option key={type._id} value={type._id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Capacity
                    </label>
                    <input
                      type="number"
                      value={parkingForm.capacity}
                      onChange={(e) =>
                        setParkingForm((p) => ({
                          ...p,
                          capacity: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Number of vehicles"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={parkingForm.twoWheeler}
                      onChange={(e) =>
                        setParkingForm((p) => ({
                          ...p,
                          twoWheeler: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded"
                    />
                    <span className="text-sm">Two Wheeler</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={parkingForm.fourWheeler}
                      onChange={(e) =>
                        setParkingForm((p) => ({
                          ...p,
                          fourWheeler: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded"
                    />
                    <span className="text-sm">Four Wheeler</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Parking Instructions
                  </label>
                  <textarea
                    value={parkingForm.instructions}
                    onChange={(e) =>
                      setParkingForm((p) => ({
                        ...p,
                        instructions: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={2}
                    placeholder="Any special parking instructions..."
                  />
                </div>
              </>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleSaveParking}
                disabled={loadingSection === "parking"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" /> Save
              </button>
              <button
                onClick={() => setIsEditingParking(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            {parkingForm.available ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">
                    Parking Available
                  </span>
                  {parkingForm.type && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                      {PARKING_TYPES.find((t) => t._id === parkingForm.type)
                        ?.name || parkingForm.type}
                    </span>
                  )}
                </div>
                <div className="flex gap-3 text-sm text-gray-600">
                  {parkingForm.twoWheeler && <span>✓ Two Wheeler</span>}
                  {parkingForm.fourWheeler && <span>✓ Four Wheeler</span>}
                  {parkingForm.capacity && (
                    <span>Capacity: {parkingForm.capacity}</span>
                  )}
                </div>
                {parkingForm.instructions && (
                  <p className="text-sm text-gray-600 mt-2">
                    <span className="font-medium">Instructions:</span>{" "}
                    {parkingForm.instructions}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">
                No parking information available
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSection;
